import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Checkbox, Form, Icon, message, Table, Upload } from 'antd';
import {
    CompanySearchInfo,
    CompanyValidationResult,
    GroupsByCategories,
    PageResponse
} from 'components/context/types';

import styled from 'styled-components';
import {
    apiCheckValidityForAddition,
    apiSaveCompaniesToGroup
} from 'api/groups';
import t from 'utils/translations';
import CompareInput from 'components/companysearch/CompareCompanySearch';
import { union, uniqBy } from 'lodash';
import ModalBase from 'components/common/ModalBase';
import theme from 'theme';
import { ValidationResult } from 'components/common/modal/ModalCommon';
import { formatBusinessId, formatStatementDate } from 'utils/formatting';
import GroupTreeSelector from 'components/CommonGroupAndCategoryStuff/GroupTreeSelector';
import config from 'config';

interface CheckBoxProps {
    selectAction: (e: any) => void;
    checked: boolean;
    disabled: boolean;
}

const Name = styled.span`
    font-size: 12px;
`;

const GreyBusinessId = styled.span`
    font-size: 10px;
    color: grey;
`;

const TitleContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

const CompaniesTable = styled(Table)`
    .ant-table-title {
        border-left: solid ${theme.colors.blue};
    }
`;

const columns = [
    {
        title: t('COMPANY'),
        dataIndex: 'companyName',
        render: (name: string | undefined, row: any) => {
            const hyphenated = formatBusinessId(row.businessId);
            return name ? (
                <>
                    <Name>{name}</Name>
                    <br />
                    <GreyBusinessId>{hyphenated}</GreyBusinessId>
                </>
            ) : (
                <Name>{row.businessId}</Name>
            );
        }
    },
    {
        title: t('VALIDATION_ERROR_MESSAGE'),
        dataIndex: 'validationResult',
        render: (validationResult: string) => {
            {
                return (
                    (validationResult && validationResult !== 'OK' && (
                        <ValidationResult>
                            {t(validationResult)}
                        </ValidationResult>
                    )) || <></>
                );
            }
        }
    },
    {
        title: t('LATEST_STATEMENT'),
        dataIndex: 'statementDate',
        render: (date: string) => (date ? formatStatementDate(date) : '')
    },
    {
        title: t('ADD'),
        dataIndex: 'actions',
        align: 'center' as 'left' | 'right' | 'center',
        width: '70px',
        render: (props: CheckBoxProps) => {
            if (props) {
                const { selectAction, checked, disabled } = props;
                return (
                    <Checkbox
                        onChange={e => selectAction(e)}
                        checked={checked}
                        disabled={disabled}
                    />
                );
            }
            return <></>;
        }
    }
];

interface Props {
    preSelectedGroupId?: string;
    groupsByCategories: GroupsByCategories[];
    visible: boolean;
    closeModal: () => void;
    afterSuccess: () => void;
}

function CompanyToGroupModal(props: Props) {
    const [groupId, setGroupId] = useState<string | undefined>(undefined);
    const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
    const [selectableCompanies, setSelectableCompanies] = useState<
        CompanyValidationResult[]
    >([]);
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [fileList, setFileList] = useState<any[]>([]);
    const [nullify, setNullify] = useState<boolean>(false);

    useEffect(() => {
        buildDataSource();
    }, [selectedCompanies, selectableCompanies]);

    useEffect(() => {
        setGroupId(props.preSelectedGroupId);
    }, [props.preSelectedGroupId, props.visible]);

    // This only toggles the value, the value itself has no 'meaning'. CompareSearch -component
    // checks changes in this prop and nullifies the search field if changed
    const emptySearchField = () => {
        setNullify(!nullify);
    };

    const onSelect = (value: string) => {
        setGroupId(value);
        setSelectableCompanies([]);
        setFileList([]);
        emptySearchField();
    };
    const onOk = () => {
        if (!groupId) {
            message.warn(t('GROUP_BEFORE_ADDITION_WARNING'));
            return;
        }
        apiSaveCompaniesToGroup(selectedCompanies, groupId)
            .then((response: Response) => {
                if (response.status === 200) {
                    if (selectedCompanies.length === 0) {
                        message.info(t('NO_COMPANIES_ADDED'));
                    } else {
                        message.success(t('ADDITION_SUCCESSFUL'));
                    }
                } else {
                    message.error(t('ADDITION_FAILURE'));
                }
            })
            .then(() => {
                setSelectableCompanies([]);
                setSelectedCompanies([]);
                props.afterSuccess();
                props.closeModal();
            })
            .catch(({ status }) => {
                message.error(t('ADDITION_FAILURE_TRY_AGAIN'));
            });
    };

    const select = (businessId: string, e: any) => {
        if (e.target.checked) {
            addToSelectedCompanies(businessId);
        } else {
            removeFromSelectedCompanies(businessId);
        }
    };

    const addToSelectedCompanies = (businessId: string): void => {
        if (!selectedCompanies.includes(businessId)) {
            const comps = [...selectedCompanies];
            comps.push(businessId);
            setSelectedCompanies(comps);
        }
    };

    const removeFromSelectedCompanies = (businessId: string): void => {
        const idx = selectedCompanies.indexOf(businessId);
        if (idx > -1) {
            const comps = [...selectedCompanies];
            comps.splice(idx, 1);
            setSelectedCompanies(comps);
        }
    };

    const setSearchResult = (result: CompanySearchInfo) => {
        if (!groupId) {
            return;
        }
        emptySearchField();
        apiCheckValidityForAddition(
            [result.businessId.replace('-', '')],
            groupId
        )
            .then(response => response.json())
            .then((data: PageResponse<CompanyValidationResult>) => {
                const validationResult: CompanyValidationResult | undefined =
                    data.content[0];
                const selectable = [...selectableCompanies];
                selectable.push(validationResult);
                setSelectableCompanies(uniqBy(selectable, 'businessId'));
                if (validationResult.validationResult === 'OK') {
                    addToSelectedCompanies(validationResult.businessId);
                }
            });
    };

    const buildDataSource = () => {
        const res: any[] = selectableCompanies.map(
            (comp: CompanyValidationResult) => {
                return {
                    key: comp.businessId,
                    companyName: comp.companyName,
                    businessId: comp.businessId,
                    statementDate: comp.lastFinancialStatement,
                    validationResult: comp.validationResult,
                    actions: {
                        selectAction: (e: any) => select(comp.businessId, e),
                        checked: selectedCompanies.includes(comp.businessId),
                        disabled: comp.validationResult !== 'OK'
                    } as CheckBoxProps
                };
            }
        );
        setDataSource(res);
    };

    const onUploadStateChange = (fileUploadResponse: any) => {
        setFileList([fileUploadResponse.file]);
        setSelectedCompanies([]);
        setSelectableCompanies([]);
        if (fileUploadResponse.file.status === 'error') {
            message.error(t('ERROR_UPLOADING_FILE'));
        }
        if (fileUploadResponse.file.status === 'done') {
            const selectable = [...selectableCompanies];

            const content: CompanyValidationResult[] =
                fileUploadResponse.file.response.content;
            const all = selectable.concat(content);
            all.sort((a, b) => (a.validationResult === 'OK' ? 1 : -1));
            setSelectableCompanies(uniqBy(all, 'businessId'));

            const okCompanies = content
                .filter(comp => comp.validationResult === 'OK')
                .map(comp => comp.businessId);
            const selected = [...selectedCompanies];
            const concatted = selected.concat(okCompanies);
            setSelectedCompanies(union(concatted));
        }
    };

    const onCancel = () => {
        setSelectableCompanies([]);
        setSelectedCompanies([]);
        setFileList([]);
        emptySearchField();
        setGroupId(undefined);
        props.closeModal();
    };

    const getTableTitle = () => {
        return (
            <TitleContainer>
                <div>Lisättävät yritykset</div>
                <div>{selectedCompanies.length} kpl</div>
            </TitleContainer>
        );
    };

    return (
        <ModalBase
            title={t('ADD_COMPANY_TO_GROUP_TITLE')}
            visible={props.visible}
            onCancel={onCancel}
            onOk={onOk}
            okText={t('ADD')}
        >
            <Form>
                <Form.Item label={t('ADDITION_GROUP')}>
                    <GroupTreeSelector
                        groupsByCategories={props.groupsByCategories}
                        onSelect={onSelect}
                        value={groupId}
                    />
                </Form.Item>
                {(groupId && (
                    <>
                        <Form.Item label={t('LOAD_CSV')}>
                            <Upload
                                accept=".csv,.CSV"
                                action={`${config.backendUrl}/group/${groupId}/validate/csv`}
                                onChange={changeObj =>
                                    onUploadStateChange(changeObj)
                                }
                                showUploadList={{ showRemoveIcon: false }}
                                withCredentials={true}
                                multiple={false}
                                fileList={fileList}
                            >
                                <Button>
                                    <Icon type="upload" /> Tuo tiedosto
                                </Button>
                            </Upload>
                        </Form.Item>
                        <Form.Item label={'Lisää yksittäinen y-tunnus'}>
                            <CompareInput
                                setSearchResult={setSearchResult}
                                nullify={nullify}
                            />
                        </Form.Item>
                        <CompaniesTable
                            title={getTableTitle}
                            size="small"
                            dataSource={dataSource}
                            columns={columns}
                            scroll={{ y: 240 }}
                        />
                    </>
                )) || <></>}
            </Form>
        </ModalBase>
    );
}

export default CompanyToGroupModal;
